import { z } from "zod";

/**
 * Connection lifecycle for a Hotstuff account.
 *
 * Hotstuff uses the same agent-wallet session-key model as Hyperliquid: a
 * master wallet signs a one-off `addAgent` action, after which an agent
 * private key signs every subsequent trading action. We cache the agent
 * private key in the Parameter Store (never in the DB).
 */
export const HotstuffConnectionStatus = {
  WAITING_APPROVAL: "waitingApproval",
  ONLINE: "online",
} as const;
export type HotstuffConnectionStatus = (typeof HotstuffConnectionStatus)[keyof typeof HotstuffConnectionStatus];

/**
 * Response shape of `POST /v1/exchange-account/hotstuff`.
 *
 * The backend generates the agent keypair, msgpack-encodes the `addAgent`
 * action payload, keccak256-hashes it, and hands everything back to the FE
 * so the only thing the client has to do is sign the EIP-712 `Action`
 * envelope with the connected wallet.
 *
 * - `agentAddress` — agent wallet address (lowercased, goes into the payload)
 * - `agentName` — human-readable name (optionally overridden by the caller)
 * - `nonce` — ms-precision nonce (server-assigned, used as-is)
 * - `validUntil` — unix **ms** expiry. Matches the upstream SDK examples
 *   (`msFromNow(N)` in TS, `int(time.time()*1000)+N_ms` in Python). The
 *   Hotstuff server compares against `now_ms` and rejects seconds-looking
 *   values as already expired with `"invalid valid until"`.
 * - `actionHash` — `0x…` 32-byte keccak256 of the msgpack-encoded payload.
 *    The FE signs `{ source, hash: actionHash, txType: ADD_AGENT }` with
 *    `HOTSTUFF_APPROVAL_DOMAIN` + `HOTSTUFF_APPROVAL_TYPES`.
 */
export const GetHotstuffAgentSchema = z.object({
  agentAddress: z.string().min(1),
  agentName: z.string().min(1),
  nonce: z.number().int().positive(),
  validUntil: z.number().int().positive(),
  actionHash: z.string().regex(/^0x[0-9a-fA-F]{64}$/) as z.ZodType<`0x${string}`>,
  prepareId: z.uuid(),
  status: z.enum(HotstuffConnectionStatus),
  /**
   * Always populated by the BE. The FE must collect a second EIP-712
   * signature over `{source, hash: brokerFeeActionHash, txType: APPROVE_BROKER_FEE}`
   * in the same approval flow and submit it as `brokerFeeSignature` alongside
   * the main agent signature. Hotstuff treats addAgent and approveBrokerFee
   * as two distinct transactions, so the BE posts them sequentially.
   * Stays optional in the schema for forward-compat with callers reading
   * historical responses.
   */
  brokerFeeActionHash: (z.string().regex(/^0x[0-9a-fA-F]{64}$/) as z.ZodType<`0x${string}`>).optional(),
});
export type GetHotstuffAgentDto = z.infer<typeof GetHotstuffAgentSchema>;

/**
 * Body of `POST /v1/exchange-account/hotstuff/approve`.
 *
 * The FE has signed the EIP-712 `Action` envelope whose `hash` field is the
 * server-computed `actionHash`. The BE reloads the prepare record by
 * `prepareId`, re-derives the message, verifies the signature recovers to the
 * authenticated wallet, then posts the pre-built action payload to Hotstuff.
 *
 * `brokerFeeSignature` is only expected when `GET /hotstuff` returned a
 * `brokerFeeActionHash` (i.e. the feature flag is on). The BE posts
 * `approveBrokerFee` right after `addAgent` succeeds. Broker-fee post
 * failures are logged but do NOT fail the authorize — the agent is already
 * created and the subsequent trade will surface the broker rejection.
 */
export const ApproveHotstuffBodySchema = z.object({
  prepareId: z.uuid(),
  signature: z
    .string()
    .regex(/^0x[0-9a-fA-F]{8,264}$/, { error: "Invalid EVM signature format." }) as z.ZodType<`0x${string}`>,
  brokerFeeSignature: (
    z.string().regex(/^0x[0-9a-fA-F]{8,264}$/, { error: "Invalid EVM signature format." }) as z.ZodType<`0x${string}`>
  ).optional(),
});
export type ApproveHotstuffBodyDto = z.infer<typeof ApproveHotstuffBodySchema>;

export const HotstuffStatusSchema = z.object({
  status: z.nativeEnum(HotstuffConnectionStatus),
});
export type HotstuffStatusDto = z.infer<typeof HotstuffStatusSchema>;
